import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { MobxComponent } from '../../../../../mobx/components/index';
import { Protable } from '@lib/components';
import Avatar from 'react-avatar';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RestaurantCustomerModal } from './modal';
import { RestaurantCustomerCreateModal } from './create';
import { config } from '../../../../../config';
import { Tooltip } from '@lib/components';
import { Button, Box } from '@lib/components';
import { FaQuestionCircle, FaPlus } from 'react-icons/fa';

/** Legacy CRM tags were a comma string; newer saves use string[]. */
function formatCustomerDashboardTags(
	tags: string | string[] | undefined
): string {
	if (tags == null) return '—';
	if (Array.isArray(tags)) {
		const parts = tags.map((x) => String(x).trim()).filter(Boolean);
		return parts.length ? parts.join(', ') : '—';
	}
	const s = String(tags).trim();
	return s || '—';
}

interface Props extends WithTranslation {}
interface State {
  createActive: boolean;
}

@inject('store')
@observer
export class RestaurantCustomersClass extends MobxComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      createActive: false,
    };
  }

  componentWillUnmount() {
    this.injected.store.updateCustomers({
      items: [],
      count: 0,
    });
  }

  createModalToggle = (createActive: boolean) => this.setState({ createActive });

  render() {
    if (!this.state) return;
    const { store, t } = this.injected;
    const { isMainReseller, restrictions } = store;
    const customers = store.customers;
    const r = store.restaurant!;
    const rid = r._id;
    return (
      <div>
        <div className="flex-l-r-center m-b-4 flex-wrap" style={{ gap: '1rem' }}>
          <div>
            <h1 className="m-b-0" style={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
              {t('Customers', { defaultValue: 'Customers' })}
            </h1>
            <p
              className="m-b-0 m-t-1"
              style={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
              }}
            >
              {t('customers.subtitle', { defaultValue: 'Guest database & reservations' })}
            </p>
          </div>
          <div className="flex-line centered flex-wrap" style={{ gap: '0.5rem' }}>
            {restrictions.restaurant.customers_actions && (
              <Tooltip text={'Create Customer'} width={120} position={'top'}>
                <Button type="button" size="sm" color="primary" onClick={() => this.createModalToggle(true)}>
                  <FaPlus className="m-r-1" /> {t('Add customer', { defaultValue: 'Add customer' })}
                </Button>
              </Tooltip>
            )}
            {isMainReseller && (
              <Tooltip text={'Help'} width={50} position={'top'}>
                <a
                  target="_blank"
                  href="https://support.cloudwaitress.com/platform-information/customer-accounts"
                  rel="noreferrer"
                  className=""
                >
                  <Button type="button" size="sm" color="white">
                    <FaQuestionCircle />
                  </Button>
                </a>
              </Tooltip>
            )}
          </div>
        </div>

        <Box
          className="m-b-2"
          style={{
            borderRadius: '1.5rem',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            overflow: 'hidden',
            background: '#fff',
          }}
        >
        {/* @ts-ignore */}
        <Protable<T.Schema.Customer.CustomerSchema>
          region={r.settings.region}
          items={customers.items}
          count={customers.count}
          exportUrl={`${config.urls.api}/exports/dashboard/customers?jwt=${store.auth.token}&rid=${
            store.restaurant!._id
          }`}
          screenWidth={store.view.screen_width}
          query={{
            sortKeys: [
              { label: 'Created', value: 'created' },
              { label: 'Last Seen', value: 'meta.last_seen' },
              { label: 'Name', value: 'details.name' },
              { label: 'Orders', value: 'stats.orders' },
              { label: 'Sales', value: 'stats.sales' },
            ],
            filters: [
              {
                label: 'Type',
                key: 'type',
                values: [
                  { label: 'Guest', value: 'guest' },
                  { label: 'E-Mail', value: 'email' },
                  { label: 'Facebook', value: 'facebook' },
                  { label: 'Google', value: 'google' },
                ],
              },
            ],
          }}
          onClick={customer => {
            store.router.push(`/restaurant/${rid}/customers?_id=${customer._id}`);
            store.notifications.mark_read_object('customer', customer._id);
          }}
          fetch={async data => {
            data.query.restaurants = store.restaurant!._id;
            const response = await store.api.customers_find(data);
            if (response.outcome) throw new Error(response.message);
            store.updateCustomers({
              items: response.items,
              count: response.count,
            });
          }}
          rowAlert={customer => customer.age_verification?.status === 'pending' ?? false}
          columns={[
            {
              title: '',
              width: 60,
              render: row => {
                if (row.avatar) {
                  return (
                    <div className="text-center line-height-0">
                      <img alt="Customer avatar" src={row.avatar} className="thumbnail-32" />
                    </div>
                  );
                } else {
                  return (
                    <div className="text-center line-height-0">
                      <Avatar name={row.details.name} email={row.details.email} size="32" />
                    </div>
                  );
                }
              },
            },
            {
              title: 'Type',
              breakpoint: 440,
              render: row => <span>{(row.type || 'guest').charAt(0).toUpperCase() + (row.type || 'guest').slice(1)}</span>,
            },
            {
              title: 'Name',
              render: row => (
                <span className="notranslate">
                  {(row.details && row.details.name) || 'N/A'}
                </span>
              ),
            },
            {
              title: 'Tags',
              breakpoint: 560,
              render: row => (
                <span className="small text-muted">
                  {formatCustomerDashboardTags(row.dashboard?.tags)}
                </span>
              ),
            },
            {
              title: 'Email',
              breakpoint: 750,
              render: row => (
                <span className="notranslate">
                  {(row.details && row.details.email) || 'N/A'}
                </span>
              ),
            },
            {
              title: 'Verified',
              breakpoint: 1100,
              render: row => <span>{row.verified ? 'Yes' : 'No'}</span>,
            },
            {
              title: 'Created',
              breakpoint: 1250,
              render: row => <span>{t('dateFromTimestamp', { value: row.created })}</span>,
            },
            {
              title: 'Last Seen',
              breakpoint: 1350,
              render: row => (
                <span>{row.meta?.last_seen ? t('datetimeFromTimestamp', { value: row.meta?.last_seen }) : 'N/A'}</span>
              ),
            },
            {
              title: 'Sessions',
              breakpoint: 900,
              render: row => <span>{row.stats?.sessions ? row.stats?.sessions : 0}</span>,
            },
            {
              title: 'Orders',
              breakpoint: 600,
              render: row => <span>{row.stats?.orders ? row.stats?.orders : 0}</span>,
            },
            {
              title: 'Sales',
              breakpoint: 500,
              render: row => <span>{t('currency', { value: row.stats?.sales ? row.stats?.sales : 0 })}</span>,
            },
            {
              title: 'Promos',
              breakpoint: 1000,
              render: row => <span>{row.stats?.promos_count ? row.stats?.promos_count : 0}</span>,
            },
          ]}
        />
        </Box>

        <RestaurantCustomerModal />

        <RestaurantCustomerCreateModal active={this.state.createActive} close={() => this.createModalToggle(false)} />
      </div>
    );
  }
}

export const RestaurantCustomers = withTranslation()(RestaurantCustomersClass);
